import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Download, ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 8;

export function PaymentSuccess() {
  const { lang } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | error

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        const d = await res.json();
        if (d.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(d.payment_status)) { setState("error"); return; }
      } catch (e) { /* retry */ }
      tries += 1;
      if (tries >= MAX) { setState("error"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const it = lang === "it";
  return (
    <section className="min-h-[80vh] flex items-center justify-center bg-surface px-5 pt-24" data-testid="payment-success">
      <div className="max-w-lg w-full text-center bg-white rounded-[2rem] border border-line p-10">
        {state === "checking" && (<>
          <Loader2 size={48} className="mx-auto text-tricolor-green animate-spin" />
          <h1 className="mt-6 font-display font-extrabold text-2xl">{it ? "Verifica pagamento..." : "Verifying payment..."}</h1>
        </>)}
        {state === "paid" && (<>
          <CheckCircle2 size={56} className="mx-auto text-emerald-500" />
          <h1 className="mt-6 font-display font-extrabold text-3xl text-ink">{it ? "Pagamento riuscito!" : "Payment successful!"}</h1>
          <p className="mt-3 text-muted">{it ? "Il tuo Business Plan RISORICE® è pronto. Scaricalo qui sotto — te lo abbiamo inviato anche via email." : "Your RISORICE® Business Plan is ready. Download it below — we also emailed it to you."}</p>
          <a href={`${API}/business-plan/download/${sessionId}`} className="btn-primary mt-8 inline-flex" data-testid="bp-download-btn">
            <Download size={18} /> {it ? "Scarica il Business Plan (PDF)" : "Download the Business Plan (PDF)"}
          </a>
        </>)}
        {state === "error" && (<>
          <XCircle size={56} className="mx-auto text-tricolor-red" />
          <h1 className="mt-6 font-display font-extrabold text-2xl text-ink">{it ? "Qualcosa è andato storto" : "Something went wrong"}</h1>
          <p className="mt-3 text-muted">{it ? "Non siamo riusciti a confermare il pagamento. Se l'importo è stato addebitato, contattaci." : "We couldn't confirm the payment. If you were charged, contact us."}</p>
        </>)}
        <div className="mt-8">
          <Link to="/simulatore" className="text-sm text-tricolor-green font-semibold inline-flex items-center gap-1"><ArrowLeft size={15} /> {it ? "Torna al simulatore" : "Back to simulator"}</Link>
        </div>
      </div>
    </section>
  );
}

export function PaymentCancel() {
  const { lang } = useLang();
  const it = lang === "it";
  return (
    <section className="min-h-[80vh] flex items-center justify-center bg-surface px-5 pt-24" data-testid="payment-cancel">
      <div className="max-w-lg w-full text-center bg-white rounded-[2rem] border border-line p-10">
        <XCircle size={56} className="mx-auto text-muted" />
        <h1 className="mt-6 font-display font-extrabold text-2xl text-ink">{it ? "Pagamento annullato" : "Payment cancelled"}</h1>
        <p className="mt-3 text-muted">{it ? "Nessun addebito effettuato. Puoi riprovare quando vuoi." : "No charge was made. You can try again anytime."}</p>
        <Link to="/simulatore" className="btn-primary mt-8 inline-flex" data-testid="back-to-sim"><ArrowLeft size={18} /> {it ? "Torna al simulatore" : "Back to simulator"}</Link>
      </div>
    </section>
  );
}
